import { Box } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

// KeniOrb — the "orb ball" you talk to. A brand-safe take on Keni: a breathing
// coral disc with concentric rings. No gradients, no box-shadows (per the
// KareHero brand), so it holds up as a wrapped component that owns its own
// styling rather than per-instance sx.
//
// The motion makes the state obvious:
//   speaking  — energetic: the disc bounces quickly, no rings (it's projecting).
//   listening — calm: barely-there breath with two slow sonar rings, receptive
//               and waiting.
//   idle      — a slow breath, no rings.
//
// The middle of it says what pressing it does: a microphone to start, a stop
// square while it is recording. It was a third disc, which looked like depth
// and said nothing, and the orb is a button, so the one thing it should carry
// is what happens next.
// Keyframes are declared inline in sx (no @mui/system import) so the animation
// travels with the component.

export type KeniOrbState = 'idle' | 'listening' | 'speaking';

export type KeniOrbProps = {
  /** Diameter of the solid disc in px. Rings extend beyond it. */
  size?: number;
  state?: KeniOrbState;
};

const KeniOrb = ({ size = 96, state = 'idle' }: KeniOrbProps) => {
  const speaking = state === 'speaking';
  const listening = state === 'listening';

  // Rings are the listening signature; talking is carried by the disc bounce.
  const ringCount = listening ? 2 : 0;
  const ringDuration = 4;
  const discAnim = speaking ? 'keniBreatheSpeak' : 'keniBreatheCalm';
  // Slow, calm pulse — slower still when Keni isn't speaking.
  const discDuration = speaking ? 1.4 : listening ? 4.5 : 5.5;

  const ring = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: size,
    height: size,
    borderRadius: '50%',
    border: 2,
    borderColor: 'custom.coral.500',
    animation: `keniRipple ${ringDuration}s ease-out infinite`,
    '@keyframes keniRipple': {
      '0%': { transform: 'scale(0.55)', opacity: 0.6 },
      '100%': { transform: 'scale(2.3)', opacity: 0 },
    },
  } as const;

  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        pointerEvents: 'none',
      }}
    >
      {/* Pulse rings — staggered so they read as a steady stream. */}
      {Array.from({ length: ringCount }).map((_, i) => (
        <Box
          key={i}
          sx={{ ...ring, animationDelay: `${(ringDuration / ringCount) * i}s` }}
        />
      ))}

      {/* The disc. */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: size,
          height: size,
          borderRadius: '50%',
          bgcolor: 'custom.coral.600',
          animation: `${discAnim} ${discDuration}s ease-in-out infinite`,
          // Speaking bounces hard and fast; calm barely moves.
          '@keyframes keniBreatheSpeak': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.11)' },
          },
          '@keyframes keniBreatheCalm': {
            '0%, 100%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.04)' },
          },
        }}
      >
        {/* Lighter inner discs give depth without a gradient. */}
        <Box
          sx={{
            position: 'absolute',
            borderRadius: '50%',
            bgcolor: 'custom.coral.500',
            inset: `${size * 0.16}px`,
          }}
        />
        {/* Where the innermost disc was, at the size it was. Floral white on
            coral, which is the pairing the chat's send button already uses. */}
        <Box
          sx={{
            position: 'absolute',
            inset: `${size * 0.34}px`,
            display: 'grid',
            placeItems: 'center',
            color: 'custom.neutral.50',
          }}
        >
          <Icon
            icon={(listening ? 'stop' : 'microphone') as never}
            type='fas'
            fontSize={Math.round(size * 0.26)}
          />
        </Box>
      </Box>
    </Box>
  );
};

KeniOrb.displayName = 'KeniOrb';
export default KeniOrb;
